/**
 * ServiceSummaryWidget — Iter-92 · Field agent shift close-out.
 *
 * Every field agent submits one summary per assigned property per shift.
 * Fields: Date, Time, Location (assigned-property dropdown), Quantity.
 *
 * Scope on the read side (server enforces role gates):
 *   • Site Agent / Crew Leader → own submissions
 *   • Supervisor → all crews they oversee
 *   • Regional Manager → region-wide roll-up
 *   • Ops Director / Admin / CEO → company-wide totals
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ClipboardCheck, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ServiceSummaryWidget({ properties = [] }) {
  const today = new Date().toISOString().slice(0, 10);
  const now = new Date().toTimeString().slice(0, 5);
  const [form, setForm] = useState({
    date: today,
    shift_time: now,
    property_id: properties[0]?.property_id || '',
    quantity: '',
    service_type: '',
  });
  const [serviceTypes, setServiceTypes] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/commercial-quote/service-types`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.types) setServiceTypes(d.types); })
      .catch(() => {});
  }, []);
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    if (!form.property_id && properties[0]?.property_id) {
      setForm((f) => ({ ...f, property_id: properties[0].property_id }));
    }
  }, [properties, form.property_id]);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/service-summary?date_from=${today}&date_to=${today}`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setMine(d.summaries || []);
      }
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) return toast.error('Pick a property');
    const qty = parseInt(form.quantity, 10);
    if (!Number.isFinite(qty) || qty < 0) return toast.error('Quantity must be a non-negative number');
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/service-summary`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, quantity: qty }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) return toast.error(data.detail || 'Save failed');
      toast.success('Service summary saved');
      setForm((f) => ({ ...f, quantity: '' }));
      load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const totalToday = useMemo(
    () => (mine || []).reduce((n, r) => n + Number(r.quantity || 0), 0),
    [mine],
  );

  return (
    <Card className="bg-zinc-900 border border-[#C9A962]/40 p-4" data-testid="service-summary-widget">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-[#C9A962]" />
          <h3 className="text-white font-black text-base" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            SERVICE SUMMARY · SHIFT CLOSE-OUT
          </h3>
        </div>
        <Badge className="bg-zinc-800 text-[10px] uppercase tracking-widest">
          Today: {totalToday} units
        </Badge>
      </div>

      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end" data-testid="service-summary-form">
        <Field label="Date">
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            className="bg-zinc-900 border-zinc-700 text-white text-xs h-9"
            data-testid="service-summary-date"
          />
        </Field>
        <Field label="Time">
          <Input
            type="time"
            value={form.shift_time}
            onChange={(e) => setForm((f) => ({ ...f, shift_time: e.target.value }))}
            className="bg-zinc-900 border-zinc-700 text-white text-xs h-9"
            data-testid="service-summary-time"
          />
        </Field>
        <Field label="Location (Property)">
          <select
            value={form.property_id}
            onChange={(e) => setForm((f) => ({ ...f, property_id: e.target.value }))}
            className="w-full h-9 px-2 rounded bg-zinc-900 border border-zinc-700 text-white text-xs focus:outline-none focus:border-[#C9A962]"
            data-testid="service-summary-property"
          >
            {properties.length === 0 && <option value="">No assigned properties</option>}
            {properties.map((p) => (
              <option key={p.property_id} value={p.property_id}>
                {p.property_name || p.property_id}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Service Type (Commercial)">
          <select
            value={form.service_type}
            onChange={(e) => setForm((f) => ({ ...f, service_type: e.target.value }))}
            className="w-full h-9 px-2 rounded bg-zinc-900 border border-zinc-700 text-white text-xs focus:outline-none focus:border-[#C9A962]"
            data-testid="service-summary-service-type"
          >
            <option value="">— Valet / N/A —</option>
            {serviceTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Units Serviced">
          <div className="flex gap-1.5">
            <Input
              type="number"
              min={0}
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              placeholder="0"
              className="bg-zinc-900 border-zinc-700 text-white text-xs h-9"
              data-testid="service-summary-quantity"
            />
            <Button
              type="submit"
              size="sm"
              disabled={saving}
              className="h-9 px-3 text-xs font-bold"
              style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
              data-testid="service-summary-submit"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              {saving ? '' : 'Log'}
            </Button>
          </div>
        </Field>
      </form>

      {mine.length > 0 && (
        <div className="mt-3 border-t border-zinc-800 pt-2 space-y-1" data-testid="service-summary-today-list">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Today&apos;s Submissions</p>
          {mine.map((s) => (
            <div key={s.summary_id} className="flex items-center justify-between text-[11px] text-zinc-300 py-0.5">
              <span className="truncate">{s.property_name || s.property_id} · {s.shift_time}</span>
              <span className="font-mono text-white">{s.quantity} units</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 font-semibold">{label}</p>
      {children}
    </div>
  );
}
